import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { JwtUser } from '@renovationman/ts-utils';
import {
  AuthorizationRequest,
  decodeJwtPayload,
  OAuthCallbackResult,
  OAuthFlowService,
} from 'libs/auth-lib/src/public-api';

import {
  AUTH_CALLBACK_PATH,
  PORTAL_OAUTH_CLIENT_ID,
  PORTAL_OAUTH_SCOPE,
} from '~constants';

import { rmRedirect } from '../../services/rm-redirect';
import { UserDestinationService } from '../../services/user-destination.service';

/**
 * Le retour du portail après authentification — l'aiguillage de la story O2.2.
 *
 * <strong>Pourquoi cette route existe, et pourquoi ici.</strong> Le site public envoie tout le monde
 * sur `login.renovationman.com` par un bouton unique, sans `type` ni `originUrl` : clients, artisans,
 * employés et partenaires arrivent par la même porte, sans contexte applicatif. Quelqu'un doit décider
 * où les envoyer, et en OAuth 2 ce ne peut pas être le serveur d'autorisation — il revient toujours sur
 * la `redirect_uri` que le client a fournie et qu'il a validée contre sa liste blanche. Ce n'est pas
 * une limitation à contourner, c'est ce qui ferme la redirection ouverte. La décision appartient donc
 * au portail, qui est lui-même un client OAuth enregistré.
 *
 * <strong>Le type est lu dans le jeton d'identité.</strong> La claim `user` y a été posée exprès pour
 * ça (uaa !474) : l'`id_token` est le jeton que le client est censé lire, là où le jeton d'accès
 * s'adresse aux API. Aucun aller-retour supplémentaire n'est nécessaire.
 *
 * <strong>Ce que cette route ne fait volontairement pas.</strong> Elle ne range aucun jeton. Le portail
 * n'appelle aucune API métier — sa portée est `openid` seule — et un jeton du portail rangé ici ferait
 * court-circuiter `ngOnInit` de la page de login aux visites suivantes.
 *
 * ⚠️ <strong>Rien n'envoie encore d'utilisateur ici.</strong> Une connexion ordinaire passe toujours par
 * le chemin hérité. Tant que les applications n'ont pas leur propre route de rappel (O2.7, O2.8),
 * l'application de destination redemandera les identifiants : elle ne sait pas encore amorcer son flux,
 * que la session SSO rendrait pourtant silencieux.
 */
@Component({
  template: '',
  standalone: false,
})
export class AuthCallbackPage implements OnInit {
  constructor(
    private oauthFlowService: OAuthFlowService,
    private userDestinationService: UserDestinationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.oauthFlowService
      .consume(this.request, window.location.search)
      .then((result) => this.routeToUserSpace(result))
      .catch(() => this.backToLogin());
  }

  /**
   * L'origine est laissée vide : le portail **est** servi par le serveur d'autorisation, CloudFront
   * routant `/oauth2/*` vers UAA depuis ce domaine (story O2.3). Les appels partent donc en relatif,
   * et l'URI de redirection se déduit de l'origine courante plutôt que d'être configurée par
   * environnement — une valeur de moins à tenir synchronisée avec le serveur.
   */
  private get request(): AuthorizationRequest {
    return {
      issuer: '',
      clientId: PORTAL_OAUTH_CLIENT_ID,
      redirectUri: window.location.origin + AUTH_CALLBACK_PATH,
      scope: PORTAL_OAUTH_SCOPE,
    };
  }

  private routeToUserSpace(result: OAuthCallbackResult): void {
    const payload = result.tokens.id_token
      ? decodeJwtPayload(result.tokens.id_token)
      : null;
    const jwtUser = payload?.user ? new JwtUser(payload.user) : null;
    rmRedirect(this.userDestinationService.baseUrlFor(jwtUser));
  }

  /**
   * Un `state` inconnu, un code refusé, un retour en erreur : on revient au formulaire.
   *
   * Le refus est volontairement indistinguable côté utilisateur — il n'a rien à apprendre de la raison,
   * et un `state` forgé ne doit surtout pas obtenir de diagnostic. Ce qui compte est qu'il atterrisse
   * sur une page utile plutôt que sur un écran vide.
   */
  private backToLogin(): void {
    this.router.navigate(['login']);
  }
}
